import DayjsUtils from '@date-io/dayjs';
import {
  Backdrop,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import AutocompleteTree from 'components/atoms/AutocompleteTree';
import { IAutocompleteTreeRef } from 'components/atoms/AutocompleteTree/types';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import ConfirmModal from 'components/atoms/ConfirmModal';
import InputDuration from 'components/atoms/InputDuration';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import dayjs from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import dayjsDuration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useFormik } from 'formik';
import { IEquipmentFilterTree } from 'interfaces/Equipment';
import { debounce } from 'lodash';
import React from 'react';
import { useQuery } from 'react-query';
import {
  getActivityMaintenanceType,
  getYesOrNotOption,
} from 'services/activity';
import { getClusters } from 'services/cluster';
import { postEquipmentOptionsByInstallation } from 'services/equipment';
import { postExecutorOptions } from 'services/executor';
import {
  postActivity,
  postActivityFilterOptions,
} from 'services/inspection-checklist';
import { postInstallationOptions } from 'services/installation';
import { postInspectorTechnicianFilterOptions } from 'services/msgraph-users';
import * as yup from 'yup';
import useStyles from './style';
import { IModalPlanProps } from './types';

const SEARCH_GUIDE_TEXT = 'Digite 2 Caracteres ou mais para pesquisar';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';
const OPTION = 'Opcional';
const DEFAULT_TYPE_MAINTENANCE = 'Preventiva';

const FORM_ID = 'PLAN_INSPECTION';
const EXECUTOR_DEFAULT = 'Omega';

const MIN_DATE = dayjs(dayjs().format('YYYY-MM-DD'));
const MAX_DATE = dayjs('2100-01-01');

dayjs.extend(isoWeek);
dayjs.extend(dayjsDuration);

type IOption = {
  value: string | number;
  label: string;
};

const ModalPlan = ({
  open,
  onClose,
  children,
  reloadData,
}: IModalPlanProps) => {
  const classes = useStyles();

  const equipmentTreeRef = React.useRef<IAutocompleteTreeRef>(null);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [loadingSaveForm, setLoadingSaveForm] = React.useState(false);
  const [activityOptions, setActivityOptions] = React.useState<IOption[]>([]);
  const [clusterOptions, setClusterOptions] = React.useState<IOption[]>([]);
  const [installationOptions, setInstallationOptions] = React.useState<
    IOption[]
  >([]);
  const [typeMaintenanceOptions, setTypeMaintenance] = React.useState<
    IOption[]
  >([]);
  const [equipmentOptions, setEquipmentOptions] = React.useState<
    IEquipmentFilterTree[]
  >([]);
  const [executorOptions, setExecutorOptions] = React.useState<IOption[]>([]);
  const [inspectorTechnicianOptions, setInspectorTechnicianOptions] =
    React.useState<IOption[]>([]);
  const [outageOptions, setOutageOptions] = React.useState<IOption[]>([]);
  const [utilizationOptions, setUtilizationOptions] = React.useState<IOption[]>(
    [],
  );

  const [fieldsSearchText, setFieldsSearchText] = React.useState<{
    [key: string]: string;
  }>({});

  const filter = createFilterOptions<IOption>();

  const validationSchema = yup.object({
    activityId: yup.string().required(DEFAULT_OK_TEXT),
    clusterId: yup.string().required(DEFAULT_OK_TEXT),
    installationId: yup.string().required(DEFAULT_OK_TEXT),
    maintenanceId: yup.string().required(DEFAULT_OK_TEXT),
    equipmentId: yup.string().required(DEFAULT_OK_TEXT),
    start: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const test = dayjs(value);
        return (
          dayjs(value).isValid() &&
          (test.isSame(MIN_DATE) || test.isAfter(MIN_DATE))
        );
      }),
    completion: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      // eslint-disable-next-line func-names
      .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
        const test = dayjs(value);
        // eslint-disable-next-line react/no-this-in-sfc
        const { start } = this.parent;

        const validation =
          // não gera erro se o início previsto n for digitado
          !start ||
          // testa a data e verifica a validação de data mínima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          (test.isValid() &&
            (test.isSame(start, 'day') || test.isAfter(start, 'day')));

        return validation;
      }),
    duration: yup.string().nullable(),
    providerId: yup.string().required(DEFAULT_OK_TEXT),
    frequency: yup.number().positive(DEFAULT_ERROR_TEXT).nullable(),
    inspectorTechnician: yup.string().required(DEFAULT_OK_TEXT).nullable(),
    outage: yup.string().required(DEFAULT_OK_TEXT),
    utilization: yup.string().required(DEFAULT_OK_TEXT),
    planNumber: yup.string().required().typeError(DEFAULT_OK_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      activityId: '',
      clusterId: '',
      installationId: '',
      maintenanceId: '',
      equipmentId: '',
      start: null,
      completion: null,
      duration: '',
      providerId: '',
      frequency: null,
      inspectorTechnician: '',
      outage: '',
      utilization: '',
      week: dayjs().isoWeek().toString(),
      planNumber: dayjs().year(),
      completionPercentage: 0,
    },
    validationSchema,
    onSubmit: formValues => {
      let duration = 0;
      if (formValues.duration) {
        const [hours, minutes] = formValues.duration.split(':');

        duration = Number(
          dayjs
            .duration({
              hours: Number(hours),
              minutes: Number(minutes),
            })
            .asHours()
            .toFixed(2),
        );
      }

      setLoadingSaveForm(true);
      postActivity({ ...formValues, duration })
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const defaultValueForMaintenanceType = React.useMemo(() => {
    if (typeMaintenanceOptions.length > 0) {
      const maintenance = typeMaintenanceOptions.find(
        item =>
          item.label.toLowerCase() === DEFAULT_TYPE_MAINTENANCE.toLowerCase(),
      );
      if (maintenance) {
        const { label, value } = maintenance;
        return {
          label,
          value,
        };
      }
    }

    return { label: '', value: '' };
  }, [typeMaintenanceOptions]);

  const defaultValueForExecutor = React.useMemo(() => {
    if (executorOptions.length > 0) {
      const { label, value } = executorOptions[0];

      return {
        label,
        value,
      };
    }

    return { label: '', value: '' };
  }, [executorOptions]);

  const enableQueries = React.useMemo(() => open, [open]);

  const enableQueryActivityOptions = React.useMemo(() => {
    return enableQueries && Boolean(formik.values.installationId);
  }, [enableQueries, formik.values.installationId]);

  const enableClusterOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableInstallationOptions = React.useMemo(() => {
    return enableQueries && Boolean(formik.values.clusterId);
  }, [enableQueries, formik.values.clusterId]);

  const enableTypeMaintenanceOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableQueryEquipmentOptions = React.useMemo(() => {
    return enableQueries && Boolean(formik.values.installationId);
  }, [enableQueries, formik.values.installationId]);

  const enableExecutorOptions = React.useMemo(() => {
    return enableQueries;
  }, [enableQueries]);

  const enableInspectorTechnicianOptions = React.useMemo(() => {
    return (
      enableQueries &&
      Boolean(
        fieldsSearchText?.inspectorTechnician &&
          fieldsSearchText?.inspectorTechnician.length >= 2,
      )
    );
  }, [enableQueries, fieldsSearchText?.inspectorTechnician]);

  const enableOutageOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableUtilizationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryActivityOptions = useQuery({
    queryKey: [
      'ActivityOptionsContext',
      [fieldsSearchText?.activityId, formik?.values.installationId],
    ],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.activityId,
        installationId: formik?.values.installationId,
      };
      const { data } = await postActivityFilterOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setActivityOptions(data);
    },
    cacheTime: 0,
    enabled: enableQueryActivityOptions,
  });

  const queryClusterOptions = useQuery({
    queryKey: ['clusterOptionsContext'],
    queryFn: async () => {
      const { data } = await getClusters();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setClusterOptions(data);
    },
    cacheTime: 0,
    enabled: enableClusterOptions,
  });

  const queryInstallationOptions = useQuery({
    queryKey: ['installationOptionsContext', [formik.values.clusterId]],
    queryFn: async () => {
      const params = { clusterIds: [formik.values?.clusterId] };
      const { data } = await postInstallationOptions(params);
      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setInstallationOptions(data);
    },
    cacheTime: 0,
    enabled: enableInstallationOptions,
  });

  const queryMaintenanceTypeOptions = useQuery({
    queryKey: ['maintenanceTypeOptions'],
    queryFn: async () => {
      const { data } = await getActivityMaintenanceType({});

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      if (data.length > 0) {
        const maintenance = data.find(
          item =>
            item.label.toLowerCase() === DEFAULT_TYPE_MAINTENANCE.toLowerCase(),
        );

        if (maintenance) {
          const { value } = maintenance;
          formik.setFieldValue('maintenanceId', value);
        }
      }

      setTypeMaintenance(data);
    },
    cacheTime: 0,
    enabled: enableTypeMaintenanceOptions,
  });

  const queryEquipmentOptions = useQuery({
    queryKey: ['EquipmentOptionsContext', [formik?.values.installationId]],
    queryFn: async () => {
      const params = {
        installationIds: [formik?.values.installationId],
      };

      const { data } = await postEquipmentOptionsByInstallation(params);
      return data;
    },
    onSuccess: data => {
      setEquipmentOptions(data.result);
    },
    enabled: enableQueryEquipmentOptions,
  });

  const queryExecutorOptions = useQuery({
    queryKey: ['ExecutorOptionsContext'],
    queryFn: async () => {
      const params = {
        name: EXECUTOR_DEFAULT,
      };
      const { data } = await postExecutorOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      if (data.length > 0) formik.setFieldValue('providerId', data[0].value);
      setExecutorOptions(data);
    },
    cacheTime: 0,
    enabled: enableExecutorOptions,
  });

  const queryInspectorTechnicianOptions = useQuery({
    queryKey: [
      'InspectorTechnicianOptionsContext',
      [fieldsSearchText?.inspectorTechnician],
    ],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.inspectorTechnician,
      };
      const { data } = await postInspectorTechnicianFilterOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.name,
          value: option.email,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setInspectorTechnicianOptions(data);
    },
    cacheTime: 0,
    enabled: enableInspectorTechnicianOptions,
  });

  const queryOutageOptions = useQuery({
    queryKey: ['outageOptionsContext'],
    queryFn: async () => {
      // TODO: trocar para rota especifica do filtro
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setOutageOptions(data);
    },
    cacheTime: 0,
    enabled: enableOutageOptions,
  });

  const queryUtilizationOptions = useQuery({
    queryKey: ['utilizationOptionsContext'],
    queryFn: async () => {
      // TODO: trocar para rota especifica do filtro
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setUtilizationOptions(data);
    },
    cacheTime: 0,
    enabled: enableUtilizationOptions,
  });

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
    }
  }, [formik.dirty, onClose]);

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  const defaultExpandedEquipments = React.useMemo(() => {
    if (equipmentOptions.length) {
      return [equipmentOptions[0].id];
    }

    return undefined;
  }, [equipmentOptions]);

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const getInputValue = (
    inputOptions: IOption[] | undefined,
    selectedValue: string | number | undefined,
  ) => inputOptions?.find(item => item.value === selectedValue)?.label || '';

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();

      // reset all lists
      setClusterOptions([]);
      setInstallationOptions([]);
      setActivityOptions([]);
      setTypeMaintenance([]);
      setEquipmentOptions([]);
      setExecutorOptions([]);
      setInspectorTechnicianOptions([]);
      setOutageOptions([]);
      setUtilizationOptions([]);
      setFieldsSearchText({});
    }
  }, [formik, fieldsSearchText, open]);

  const renderForm = React.useCallback(
    () => (
      <form
        id={FORM_ID}
        className={classes.bodyContent}
        onSubmit={formik.handleSubmit}
      >
        <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
          <Grid className={classes.gridContent}>
            <Grid container direction="row" alignItems="center" spacing={2}>
              {/* START ROW 1 */}
              <Grid item xs={4}>
                <Autocomplete
                  id="clusterId"
                  className={classes.inputList}
                  options={clusterOptions}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      installationId: '',
                      activityId: '',
                      equipmentId: '',
                    }));

                    if (equipmentTreeRef.current)
                      equipmentTreeRef.current.onReset();

                    return handleAutoCompleteChange(
                      'clusterId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'clusterId',
                      label: 'Cluster',
                      error:
                        formik.touched.clusterId &&
                        Boolean(formik.errors.clusterId),
                      helperText:
                        (formik.touched.clusterId && formik.errors.clusterId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryClusterOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="installationId"
                  className={classes.inputList}
                  options={installationOptions}
                  disabled={!formik.values?.clusterId}
                  disableClearable={!formik.values.installationId}
                  inputValue={getInputValue(
                    installationOptions,
                    formik.values?.installationId,
                  )}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      activityId: '',
                      equipmentId: '',
                    }));

                    if (equipmentTreeRef.current)
                      equipmentTreeRef.current.onReset();
                    setEquipmentOptions([]);

                    return handleAutoCompleteChange(
                      'installationId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'installationId',
                      label: 'Instalação',
                      error:
                        formik.touched.installationId &&
                        Boolean(formik.errors.installationId),
                      helperText:
                        (formik.touched.installationId &&
                          formik.errors.installationId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryInstallationOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="activityId"
                  className={classes.inputList}
                  options={activityOptions}
                  disabled={!formik.values.installationId}
                  inputValue={
                    getInputValue(activityOptions, formik.values?.activityId) ||
                    formik.values?.activityId
                  }
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'activityId',
                      selected as IOption,
                    );
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === 'reset') {
                      formik.setFieldValue('activityId', '');
                    } else {
                      formik.setFieldValue('activityId', newValue);
                    }
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    return filtered;
                  }}
                  renderInput={params => {
                    const extraProps = {
                      name: 'activityId',
                      label: 'Título da atividade',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.activityId &&
                        Boolean(formik.errors.activityId),
                      helperText:
                        (formik.touched.activityId &&
                          formik.errors.activityId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      {
                        ...params,
                        ...extraProps,
                      },
                      queryActivityOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
            </Grid>
            {/* END ROW 1 */}

            {/* START ROW 2 */}
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <Autocomplete
                  id="maintenanceId"
                  className={classes.inputList}
                  options={typeMaintenanceOptions}
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'maintenanceId',
                      selected as IOption,
                    );
                  }}
                  disabled
                  value={defaultValueForMaintenanceType}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'maintenanceId',
                      label: 'Tipo de manutenção',
                      error:
                        formik.touched.maintenanceId &&
                        Boolean(formik.errors.maintenanceId),
                      helperText:
                        (formik.touched.maintenanceId &&
                          formik.errors.maintenanceId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryMaintenanceTypeOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteTree
                  ref={equipmentTreeRef}
                  id="equipmentId"
                  name="equipmentId"
                  defaultExpanded={defaultExpandedEquipments}
                  noOptionsText="Sem opções disponíveis"
                  className={classes.inputList}
                  disabled={!formik.values.installationId}
                  items={equipmentOptions}
                  label="Equipamento"
                  placeholder=""
                  extraTextFieldProps={{
                    error:
                      formik.touched.equipmentId &&
                      Boolean(formik.errors.equipmentId),
                    helperText:
                      (formik.touched.equipmentId &&
                        formik.errors.equipmentId) ||
                      DEFAULT_HELPER_TEXT,
                  }}
                  loading={
                    queryEquipmentOptions?.isLoading ||
                    queryEquipmentOptions?.isFetching
                  }
                  onChange={(name, treeValue) => {
                    let safeValue = {
                      label: '',
                      value: '',
                    } as IOption;

                    if (treeValue) {
                      safeValue = {
                        label: treeValue.description,
                        value: treeValue.id,
                      };
                    }

                    handleAutoCompleteChange(name, safeValue);
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <KeyboardDatePicker
                  id="start"
                  name="start"
                  label="Início previsto"
                  inputVariant="outlined"
                  format="DD/MM/YY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  onChange={date => handleDateChange('start', date)}
                  value={formik.values.start}
                  error={formik.touched.start && Boolean(formik.errors.start)}
                  helperText={
                    (formik.touched.start && formik.errors.start) ||
                    DEFAULT_HELPER_TEXT
                  }
                  clearable
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={2}>
                <KeyboardDatePicker
                  id="completion"
                  name="completion"
                  label="Término previsto"
                  inputVariant="outlined"
                  format="DD/MM/YY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  clearable
                  minDate={
                    formik.values?.start ? formik.values?.start : MIN_DATE
                  }
                  maxDate={MAX_DATE}
                  KeyboardButtonProps={{ 'aria-label': 'change date' }}
                  onChange={date => handleDateChange('completion', date)}
                  value={formik.values.completion}
                  error={
                    formik.touched.completion &&
                    Boolean(formik.errors.completion)
                  }
                  helperText={
                    (formik.touched.completion && formik.errors.completion) ||
                    DEFAULT_HELPER_TEXT
                  }
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  id="planNumber"
                  name="planNumber"
                  label="Plano"
                  variant="outlined"
                  helperText={
                    (formik.touched.planNumber && formik.errors.planNumber) ||
                    DEFAULT_HELPER_TEXT
                  }
                  onBlur={formik.handleBlur}
                  inputProps={{
                    type: 'number',
                  }}
                  onChange={event => {
                    const { value, name } = event.target;

                    if (value) {
                      formik.setFieldValue(name, Number(value));
                    } else {
                      formik.setFieldValue(name, null);
                    }
                  }}
                  value={formik.values.planNumber}
                  error={
                    formik.touched.planNumber &&
                    Boolean(formik.errors.planNumber)
                  }
                />
              </Grid>
            </Grid>
            {/* END ROW 2 */}

            {/* START ROW 3 */}
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={4}>
                <TextField
                  id="duration"
                  name="duration"
                  label="Duração prevista (HH:MM)"
                  variant="outlined"
                  onChange={formik.handleChange}
                  value={formik.values.duration}
                  error={
                    formik.touched.duration && Boolean(formik.errors.duration)
                  }
                  helperText={
                    (formik.touched.duration && formik.errors.duration) ||
                    OPTION
                  }
                  InputProps={{ inputComponent: InputDuration }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="providerId"
                  className={classes.inputList}
                  options={executorOptions}
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'providerId',
                      selected as IOption,
                    );
                  }}
                  disabled
                  value={defaultValueForExecutor}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'providerId',
                      label: 'Empresa',
                      error:
                        formik.touched.providerId &&
                        Boolean(formik.errors.providerId),
                      helperText:
                        (formik.touched.providerId &&
                          formik.errors.providerId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryExecutorOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="frequency"
                  name="frequency"
                  label="Periodicidade"
                  variant="outlined"
                  helperText={
                    (formik.touched.frequency && formik.errors.frequency) ||
                    OPTION
                  }
                  onBlur={formik.handleBlur}
                  inputProps={{
                    type: 'number',
                  }}
                  onChange={event => {
                    const { value, name } = event.target;

                    if (value) {
                      formik.setFieldValue(name, Number(value));
                    } else {
                      formik.setFieldValue(name, null);
                    }
                  }}
                  value={formik.values.frequency}
                  error={
                    formik.touched.frequency && Boolean(formik.errors.frequency)
                  }
                />
              </Grid>
            </Grid>
            {/* END ROW 3 */}
            {/* START ROW 4 */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Autocomplete
                  id="inspectorTechnician"
                  className={classes.inputList}
                  options={inspectorTechnicianOptions}
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'inspectorTechnician',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={
                    !fieldsSearchText?.executorId
                      ? SEARCH_GUIDE_TEXT
                      : NO_OPTIONS_TEXT
                  }
                  renderInput={params => {
                    const extraProps = {
                      name: 'inspectorTechnician',
                      label: 'Executor',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.inspectorTechnician &&
                        Boolean(formik.errors.inspectorTechnician),
                      helperText:
                        (formik.touched.inspectorTechnician &&
                          formik.errors.inspectorTechnician) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryInspectorTechnicianOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
            </Grid>
            {/* END ROW 4 */}
            {/* START ROW 5 */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl
                  onChange={formik.handleChange}
                  error={formik.touched.outage && Boolean(formik.errors.outage)}
                >
                  <FormLabel className={classes.formLabel} component="legend">
                    <span className={classes.formInput}>
                      A atividade possui indisponibilidade de geração?
                    </span>
                    {queryOutageOptions?.isLoading && (
                      <span>
                        <CircularProgress color="inherit" size={25} />
                      </span>
                    )}
                  </FormLabel>
                  <RadioGroup id="outage" name="outage">
                    {outageOptions.map(({ value, label }) => (
                      <FormControlLabel
                        value={value}
                        control={<Radio color="primary" />}
                        label={label}
                      />
                    ))}
                    <FormHelperText id="outage">
                      {(formik.touched.outage && formik.errors.outage) ||
                        DEFAULT_HELPER_TEXT}
                    </FormHelperText>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            {/* END ROW 5 */}
            {/* START ROW 6 */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl
                  onChange={formik.handleChange}
                  error={
                    formik.touched.utilization &&
                    Boolean(formik.errors.utilization)
                  }
                >
                  <FormLabel className={classes.formLabel} component="legend">
                    <span className={classes.formInput}>
                      A atividade vai ser realizada em aproveitamento?
                    </span>
                    {queryUtilizationOptions?.isLoading && (
                      <span>
                        <CircularProgress color="inherit" size={25} />
                      </span>
                    )}
                  </FormLabel>
                  <RadioGroup id="utilization" name="utilization">
                    {utilizationOptions.map(({ value, label }) => (
                      <FormControlLabel
                        value={value}
                        control={<Radio color="primary" />}
                        label={label}
                      />
                    ))}
                    <FormHelperText id="utilization">
                      {(formik.touched.utilization &&
                        formik.errors.utilization) ||
                        DEFAULT_HELPER_TEXT}
                    </FormHelperText>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            {/* END ROW 6 */}
          </Grid>
        </MuiPickersUtilsProvider>
      </form>
    ),
    [
      classes.bodyContent,
      classes.gridContent,
      classes.inputList,
      classes.formLabel,
      classes.formInput,
      formik,
      clusterOptions,
      installationOptions,
      activityOptions,
      typeMaintenanceOptions,
      defaultValueForMaintenanceType,
      defaultExpandedEquipments,
      equipmentOptions,
      queryEquipmentOptions?.isLoading,
      queryEquipmentOptions?.isFetching,
      executorOptions,
      defaultValueForExecutor,
      inspectorTechnicianOptions,
      fieldsSearchText?.executorId,
      queryOutageOptions?.isLoading,
      outageOptions,
      queryUtilizationOptions?.isLoading,
      utilizationOptions,
      handleAutoCompleteChange,
      inputRenderer,
      queryClusterOptions?.isLoading,
      queryInstallationOptions?.isLoading,
      filter,
      handleFieldSearchTextChange,
      queryActivityOptions?.isLoading,
      queryMaintenanceTypeOptions?.isLoading,
      handleDateChange,
      queryExecutorOptions?.isLoading,
      queryInspectorTechnicianOptions?.isLoading,
    ],
  );

  return (
    <>
      {children}
      <ConfirmModal
        title="Confirmar Saída"
        message="É possível que as alterações feitas não sejam salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <Modal
        BackdropComponent={props => <Backdrop {...props} invisible />}
        open={open}
        onClose={onCloseModal}
        className={classes.presentation}
      >
        <div className={classes.modalPresentation}>
          <div className={classes.modalCard}>
            <div className={classes.modalHeader}>
              <div className={classes.modalHeaderItem}>
                <ArrowBackIcon
                  className={classes.headerIcon}
                  onClick={onCloseModal}
                />
                <Typography variant="h3" className={classes.headerTitle}>
                  Planejar inspeção
                </Typography>
              </div>
              <div className={classes.modalHeaderItem}>
                <ButtonWithLoading
                  disabled={!formik.dirty || !formik.isValid || loadingSaveForm}
                  variant="contained"
                  color="primary"
                  type="submit"
                  form={FORM_ID}
                  isLoading={loadingSaveForm}
                >
                  Salvar
                </ButtonWithLoading>
              </div>
            </div>
            <div className={classes.modalBody}>{renderForm()}</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalPlan;
